import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'

import { Button } from '@/src/elements/button/default'
import { Header, type THeaderProps } from '@/src/elements/header/Header'
import { Stack } from '@/src/elements/navigation/Stack'
import { CommunityAdminButton } from '@/src/features/community/ui/screens/CommunityScreen/CommunityAdminButton'
import { usePublicationQuery } from '@/src/features/publication/services/usePublicationQuery'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { RequiredRole } from '@/src/general/role-access'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import { PublicationUpdate } from '../../../components/PublicationUpdate'
import { useSharePublication } from '../../../hooks/useSharePublication'

type TPublicationHeaderProps = {
  publicationId: number
}

const PublicationHeaderBar = ({ publicationId }: TPublicationHeaderProps): ReactNode => {
  const router = useRouter()
  const { routes } = useRoutes()
  const { share } = useSharePublication()
  const { data: publication } = usePublicationQuery(publicationId)

  // Should never happen because usePublicationQuery is ran in the parent component.
  if (!publication) return null

  const { community } = publication

  // If coming from another page of the site, the 'back' button should allow you to return to the previous page.
  const handleContinue = () => {
    if (router.canDismiss()) {
      router.dismiss()
    } else {
      router.replace(routes['prayer-space']())
    }
  }

  const leftButtons: THeaderProps['leftButtons'] = [
    {
      type: 'button',
      component: () => (
        <Button
          size="md"
          isIconOnly
          colors="primary"
          variant="faded"
          startIcon={ICON.ARROW_LEFT}
          onPress={handleContinue}
          testID={TEST_ID.PUBLICATION.HEADER.BACK}
        />
      ),
    },
  ]

  const rightButtons: THeaderProps['rightButtons'] = [
    {
      type: 'button',
      component: () => (
        <RequiredRole permission="canManageContent" permissionsMap={community}>
          <CommunityAdminButton
            {...community}
            onPress={() => router.navigate(routes.editPublication(publication.id))}
            testID={TEST_ID.PUBLICATION.OPTIONS.MANAGE_CONTENT}
          />
        </RequiredRole>
      ),
    },
    {
      type: 'button',
      component: () => (
        <Button
          isIconOnly
          size="md"
          colors="primary"
          variant="faded"
          startIcon={ICON.SHARE}
          onPress={() => share(publication, TRACKING_SOURCE.PUBLICATION_HEADER)}
          testID={TEST_ID.PUBLICATION.HEADER.SHARE}
        />
      ),
    },
    {
      type: 'button',
      component: () => (
        <PublicationUpdate
          publicationId={publication.id}
          isFavorite={publication.isFavorite}
          community={community}
          buttonColors="primary"
          buttonVariant="faded"
          testID={TEST_ID.PUBLICATION.OPTIONS.MENU}
        />
      ),
    },
  ]

  return <Header leftButtons={leftButtons} rightButtons={rightButtons} />
}

export const PublicationHeader = ({ publicationId }: TPublicationHeaderProps): ReactNode => (
  <Stack.Screen
    options={{
      headerShown: true,
      header: () => <PublicationHeaderBar publicationId={publicationId} />,
    }}
  />
)
